import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ConfigProvider } from 'antd';
import { useAnalyticsSetPageInfo } from '../../../../../cross-cutting-concerns/analytics/hooks/useAnalyticsSetPageInfo';
import { useAnalyticsSetProductInfo } from '../../../../../cross-cutting-concerns/analytics/hooks/useAnalyticsSetProductInfo';
import { FeatureFlagSelectors } from '../../../../../cross-cutting-concerns/feature-flags/state/featureFlagSelectors';
import { MachineAttachmentList } from '../../../machine-details/components/MachineAttachmentList/MachineAttachmentList';
import { selectData } from '../../state/machineDetailsPanelSelectors';
import { MachineDetailsPosition } from '../MachineDetailsPosition/MachineDetailsPosition';
import { StyledMachineDetailsPanelGcd } from './MachineDetailsPanelGcd.styles';
import { MachineDetailsOverviewGcd } from './MachineDetailsOverviewGcd/MachineDetailsOverviewGcd';
import { PageNamePrefix } from 'config/pages';
import { Tabs } from 'lib/components/Tabs/Tabs';
import { Styling } from 'app/cross-cutting-concerns/styling/Styling';
import { DependencyInjection } from 'app/cross-cutting-concerns/dependency-injection/DependencyInjection';

export enum MachineDetailsTab {
  OVERVIEW = 'OVERVIEW',
  DOCUMENTS = 'DOCUMENTS',
  POSITION_HISTORY = 'POSITION_HISTORY',
}

export interface MachineDetailsPanelGcdProps {
  id: string;
}

export const MachineDetailsPanelGcd = ({ id }: MachineDetailsPanelGcdProps): React.JSX.Element => {
  const [activeTab, setActiveTab] = useState(MachineDetailsTab.OVERVIEW);
  const features = useSelector(FeatureFlagSelectors.selectFeatureFlagConfig);
  const { t } = useTranslation();
  const machine = useSelector(selectData);
  const styling = DependencyInjection.resolve<Styling>(Styling);
  const kccAntdTheme = styling.getAntDesignKCCThemeConfig();
  const showMachineAttachments = features.MACHINE_ATTACHMENTS;
  const showPositionHistory = features.MACHINE_POSITION_HISTORY;
  const isIoTDevice = machine?.isIoTDevice;

  useAnalyticsSetProductInfo({
    productName: machine?.name,
    productMaterialNo: machine?.materialNumber,
    productSerialNo: machine?.serialNumber,
  });
  useAnalyticsSetPageInfo({
    // eslint-disable-next-line max-len
    pageName: `${PageNamePrefix.MACHINE_DETAILS}${machine?.name} ${machine?.classification} (${machine?.materialNumber} / ${machine?.serialNumber})`,
  });

  const tabs = [
    {
      label: t('machineDetails.generalInfo.overview'),
      key: MachineDetailsTab.OVERVIEW,
      children: MachineDetailsOverviewGcd(machine, id),
    },
    {
      label: t('machineDetails.generalInfo.documents'),
      key: MachineDetailsTab.DOCUMENTS,
      children: showMachineAttachments && machine && <MachineAttachmentList machine={machine} size="full-width" />,
    },
    ...(isIoTDevice && showPositionHistory
      ? [
          {
            label: t('machineDetailsPanel.tabs.positionHistory'),
            key: MachineDetailsTab.POSITION_HISTORY,
            children: (
              <ConfigProvider
                theme={kccAntdTheme}
                table={{ className: 'kcc-theme-table' }}
                rangePicker={{ className: 'kcc-theme-range-picker' }}
              >
                <MachineDetailsPosition machineId={id} />
              </ConfigProvider>
            ),
          },
        ]
      : []),
  ];

  const handleTabChange = (activeTabKey: string): void => {
    setActiveTab(activeTabKey as MachineDetailsTab);
  };

  return (
    <StyledMachineDetailsPanelGcd>
      <Tabs destroyInactiveTabPane={true} items={tabs} onChange={handleTabChange} activeKey={activeTab} />
    </StyledMachineDetailsPanelGcd>
  );
};
